import * as THREE from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { uniform, uv, wgslFn } from 'three/tsl';
import type { RendererMode } from './Renderer';

export interface ContactShadowConfig {
  enabled: boolean;
  /** Radius of the dark blob in world meters. */
  radius: number;
  /** Max alpha at the center while the racer is on the ground. */
  opacity: number;
  /** Above this hover height (m) the decal has fully faded out. */
  fadeHeight: number;
  /** Sharpness of the radial falloff: 1 = linear, 2 = quadratic, etc. */
  falloffPower: number;
  /** Y offset above ground to avoid z-fighting with the track surface. */
  groundOffset: number;
}

export const DEFAULT_CONTACT_SHADOW: ContactShadowConfig = {
  enabled: true,
  radius: 2.6,
  opacity: 0.55,
  fadeHeight: 2.2,
  falloffPower: 2.0,
  groundOffset: 0.05,
};

const makeNodeUniform = uniform as any;
const makeNodeUv = uv as any;
const makeWgslFn = wgslFn as any;

interface WebGPUContactShadowUniforms {
  uOpacity: { value: number };
  uFalloff: { value: number };
}

const contactShadowAlphaNode = makeWgslFn(`
fn contact_shadow_alpha(texCoord: vec2<f32>, opacity: f32, falloff: f32) -> f32 {
  let radius = length(texCoord - vec2<f32>(0.5, 0.5)) * 2.0;
  return pow(clamp(1.0 - radius, 0.0, 1.0), falloff) * opacity;
}
`);

/**
 * A scene-level dark blob that follows the racer to anchor it to the track.
 * It does NOT replace the real cast shadow from the sun's shadow map — it
 * stacks on top of it for the "anchored to the road" feel reference racers
 * get from a tighter shadow texel + a baked decal. Lives in the scene (not
 * parented to the racer) so it stays flat on the ground regardless of racer
 * banking/pitching.
 */
export class ContactShadow {
  readonly mesh: THREE.Mesh;
  readonly config: ContactShadowConfig;
  private readonly material: THREE.ShaderMaterial | MeshBasicNodeMaterial;

  constructor(initial: Partial<ContactShadowConfig> = {}, rendererMode: RendererMode = 'webgl2') {
    this.config = { ...DEFAULT_CONTACT_SHADOW, ...initial };

    const geometry = new THREE.CircleGeometry(1, 48);
    geometry.rotateX(-Math.PI / 2);

    this.material = rendererMode === 'webgpu'
      ? makeWebGPUContactShadowMaterial(this.config)
      : new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        depthTest: true,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
        uniforms: {
          uOpacity: { value: this.config.opacity },
          uFalloff: { value: this.config.falloffPower },
        },
        vertexShader: /* glsl */ `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */ `
          varying vec2 vUv;
          uniform float uOpacity;
          uniform float uFalloff;
          void main() {
            float r = length(vUv - vec2(0.5)) * 2.0;
            float a = pow(clamp(1.0 - r, 0.0, 1.0), uFalloff) * uOpacity;
            gl_FragColor = vec4(0.0, 0.0, 0.0, a);
          }
        `,
      });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.name = 'racer-contact-shadow';
    this.mesh.renderOrder = -1;
    this.mesh.frustumCulled = false;
    this.mesh.scale.setScalar(this.config.radius);
    this.mesh.visible = this.config.enabled;
  }

  /**
   * Snap the decal under the racer, scale it to `radius`, and fade by
   * how high the racer is above the ground.
   */
  update(racerX: number, racerY: number, racerZ: number, groundY: number): void {
    if (!this.config.enabled) {
      this.mesh.visible = false;
      return;
    }
    this.mesh.visible = true;
    this.mesh.position.set(racerX, groundY + this.config.groundOffset, racerZ);
    this.mesh.scale.setScalar(this.config.radius);

    const hover = Math.max(0, racerY - groundY);
    const fade = Math.max(0, 1 - hover / Math.max(0.0001, this.config.fadeHeight));
    if (this.material instanceof THREE.ShaderMaterial) {
      this.material.uniforms.uOpacity.value = this.config.opacity * fade;
      this.material.uniforms.uFalloff.value = this.config.falloffPower;
    } else {
      const uniforms = this.material.userData.webgpuContactShadowUniforms as WebGPUContactShadowUniforms;
      uniforms.uOpacity.value = this.config.opacity * fade;
      uniforms.uFalloff.value = this.config.falloffPower;
    }
  }
}

function makeWebGPUContactShadowMaterial(config: ContactShadowConfig): MeshBasicNodeMaterial {
  const uniforms: WebGPUContactShadowUniforms = {
    uOpacity: makeNodeUniform(config.opacity),
    uFalloff: makeNodeUniform(config.falloffPower),
  };
  const material = new MeshBasicNodeMaterial({
    color: 0x000000,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  material.opacityNode = contactShadowAlphaNode({
    texCoord: makeNodeUv(),
    opacity: uniforms.uOpacity,
    falloff: uniforms.uFalloff,
  });
  material.userData.webgpuContactShadowUniforms = uniforms;
  return material;
}
